import { createGlobalStyle } from 'styled-components';

export default createGlobalStyle`

  /* ═══════════════════════════════════════════════════════════════════════
     DESIGN TOKENS — the single source of truth.
     Mirrors the :root block of docs/ui-audit/frontline-redesign-v2.html 1:1.
     Never write a raw hex, a px font-size or a px spacing value in a
     styled-component. If a value you need is not here, it does not exist yet.
     ═══════════════════════════════════════════════════════════════════════ */
  :root {
    /* surfaces & ink */
    --ink-900: #0F1519;
    --ink-800: #161F26;
    --ink-700: #1E2A33;
    --canvas: #EDF0F2;
    --surface: #FFFFFF;
    --surface-alt: #F7F9FA;
    --line: #E1E6EA;
    --line-strong: #CFD6DC;

    /* text */
    --text: #14202A;
    --text-muted: #5E6E7B;
    --text-faint: #8B98A3;
    --text-invert: #FFF;

    /* brand */
    --primary: #0F7C8A;
    --primary-hover: #0C6976;
    --primary-soft: #E2F2F4;
    --primary-bright: #22B8CC;
    --gold: #B08D3F;

    /* semantic */
    --ok: #157F6B;
    --ok-soft: #E2F2EE;
    --info: #1C6FB8;
    --info-soft: #E4EEF8;
    --warn: #A9661B;
    --warn-soft: #FAEEDE;
    --danger: #C0392F;
    --danger-soft: #FAE9E7;
    --danger-line: #EFC9C5;
    --danger-tint: #FFFCFC;
    --neutral: #5E6E7B;
    --neutral-soft: #EDF0F2;
    --purple: #6B4E9E;
    --purple-soft: #EFEAF7;

    /* type — families */
    --font-ui: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-mono: 'IBM Plex Mono', ui-monospace, monospace;
    --font-ar: 'IBM Plex Sans Arabic', 'IBM Plex Sans', sans-serif;
    /* Text whose language is not known until it arrives — anything a person
       typed into the employee app or an admin form. Latin first, Arabic
       behind it, so the browser picks the face per glyph: Latin from Plex
       Sans, Arabic from Plex Sans Arabic, and a string holding both is set
       in both. --font-ar is still the right token for a field *declared*
       Arabic; this one is for the fields that could be either. */
    --font-content: 'IBM Plex Sans', 'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

    /* type — size scale */
    --fs-title: 15px;   /* page title */
    --fs-body: 13px;    /* body, table cell, button, input */
    --fs-label: 12px;   /* form label, secondary text */
    --fs-th: 10.5px;    /* table header, eyebrow */
    --fs-mono: 12px;    /* IDs, counts, timestamps */
    --fs-micro: 11.5px; /* helper text, chips, badges */
    /* The auth screens only. Nothing inside the panel shell is ever this
       large; it exists so the three sign-in titles and the welcome line
       stop being 24px and 22px literals on no scale at all. */
    --fs-display: 20px;

    /* type — weight */
    --fw-regular: 400;
    --fw-medium: 500;
    --fw-semibold: 600;
    --fw-bold: 700;

    /* type — line height */
    --lh-body: 1.45;
    --lh-prose: 1.6;

    /* spacing scale */
    --s1: 4px;
    --s2: 8px;
    --s3: 12px;
    --s4: 16px;
    --s5: 24px;
    --s6: 32px;
    --s7: 48px;

    /* radii */
    --r-sm: 6px;
    --r-md: 10px;
    --r-pill: 999px;

    /* elevation */
    --sh-1: 0 1px 2px rgba(20, 32, 42, .05), 0 0 0 1px rgba(20, 32, 42, .04);
    --sh-2: 0 6px 20px rgba(20, 32, 42, .10), 0 0 0 1px rgba(20, 32, 42, .05);

    /* control heights */
    --h-ctrl: 36px;    /* input, textarea row, select */
    --h-btn: 34px;
    --h-btn-sm: 28px;
    --h-row: 48px;     /* table row */
    --h-thumb: 30px;   /* the table thumbnail — icon, avatar square, menu photo */
    --h-topbar: 60px;
    --h-avatar: 26px;
    --h-avatar-lg: 36px; /* the design's .avatar.lg — person cards */

    /* the scroll cap on a control's floating panel — select, multi-select,
       tree-select. Roughly seven rows, so a longer list reads as scrollable
       instead of covering the card underneath it. */
    --h-overlay-max: 264px;

    /* shell metrics */
    --sidebar-w: 248px;
    --sidebar-w-min: 64px;

    /* stacking. One layer for every panel a control floats over the page —
       above cards and tables, below MiniPopUp's popovers and the modals. */
    --z-overlay: 60;
    /* The modal layer, above everything else the panel draws. */
    --z-modal: 200;

    /* The ground behind a modal. Ink rather than the old #4b576c at 80%,
       which was a blue-grey wash the palette has no other use for. */
    --scrim: rgba(15, 21, 25, .55);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     DEPRECATED — legacy aliases. DO NOT USE IN NEW CODE.

     Two groups, both temporary scaffolding for the redesign migration:

     1. Six variables that were referenced 104 times across src/ but were
        never declared anywhere (--dark, --green-dark, --dark-light, --dark-3,
        --green, --blue-light3). Until this rewrite they resolved to nothing
        and the declaration was dropped by the browser.
     2. The old 15-token palette, repointed at the new tokens so that the
        ~450 existing var() call sites render the new palette instead of the
        old one while components are migrated one at a time.

     Every entry here is deleted once its last call site is restyled. Nothing
     below defines a colour of its own — they are all pointers.
     ═══════════════════════════════════════════════════════════════════════ */
  :root {
    /* group 1 — previously undefined */
    --dark: var(--text);
    --dark-light: var(--text-muted);
    --dark-3: var(--text-muted);
    --green: var(--ok);
    --green-dark: var(--ok);
    --blue-light3: var(--primary-soft);

    /* group 2 — old palette */
    --primary-dark: var(--ink-900);
    --primary-dark-two: var(--text);
    --primary-green: var(--primary);
    --primary-two: var(--info);
    --white: var(--surface);
    --black: var(--ink-900);
    --light: var(--line-strong);
    --gray: var(--text-faint);
    --gray1: var(--surface-alt);
    --gray2: var(--neutral-soft);
    --gray3: var(--line);
    --gray4: var(--line);
    --back-color: var(--canvas);
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  body {
    font-family: var(--font-ui);
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    color: var(--text);
    background: var(--canvas);
    -webkit-font-smoothing: antialiased;
    padding: 0;
  }

  *,
  p,
  ul {
    padding: 0;
    margin: 0;
  }

  a {
    text-decoration: none;
  }

  :focus-visible {
    outline: 2px solid var(--primary-bright);
    outline-offset: 2px;
    border-radius: var(--r-sm);
  }

  /* DEPRECATED — physical text-alignment utilities. ~150 table cells still
     depend on these; remove once AppTable drives alignment from column type
     (Step 3). */
  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  .flex-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.disabled {
    cursor: not-allowed;
  }

  .text-wrap {
    word-wrap: break-word;
    display: inline-block;
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none !important;
      animation: none !important;
    }
  }
`;
